<template>
    <div id="users_wrapper">

        <v-navigation-drawer 
            v-model="createUserDialog" 
            temporary
            right
            width="46%"
            overlay-opacity="0"
            class="drawer-shadow"
        >
            <CreateUser 
                :AssignModules="assign_modules"
                :States="states"
                :Titles="titles"
                @saveCreateData="saveData" 
                @closeCreate="createUserDialog = !createUserDialog"
            />
        </v-navigation-drawer>

        <v-navigation-drawer
            v-model="mergeUsersDialog"
            temporary
            right
            width="52%"
            overlay-opacity="0"
            class="drawer-shadow"
        >
            <MergeUsers 
                v-if="mergeUsersDialog"
                :UserIds="selectedUserIds"
                @merged="usersMerged"
                @closeMerge="mergeUsersDialog = false"
            />
        </v-navigation-drawer>

        <div>
            <!-- TITLE + ROLE CHECKBOXES (LEFT) | ACTIONS (RIGHT) -->
            <div class="pt-8 px-4 d-flex align-center">
                <div class="sansation_title info--text d-inline pr-8">Users</div>
                <v-checkbox 
                    v-model="filterby.sellers" 
                    @change="applyFilters" 
                    :key="10" 
                    label="Sellers" 
                    hide-details 
                    class="adminOnly mt-0 mr-3 d-flex d-inline" 
                    color="primary darken-1">
                </v-checkbox>
                <v-checkbox 
                    v-if="isSuperAdmin"
                    v-model="filterby.admins" 
                    @change="applyFilters" 
                    :key="2" 
                    label="Admins" 
                    hide-details 
                    class="adminOnly mt-0 mr-3 d-flex d-inline" 
                    color="primary darken-1">
                </v-checkbox>
                <v-checkbox 
                    v-model="filterby.buyers" 
                    @change="applyFilters" 
                    label="Buyers" 
                    hide-details 
                    class="adminOnly mt-0 d-flex d-inline" 
                    color="primary darken-1">
                </v-checkbox>

                <v-spacer></v-spacer>

                <v-btn 
                    v-if="selectedUserIds.length > 1"
                    @click="mergeUsersDialog = true"
                    color="accent"
                    outlined
                    class="d-inline mt-2">
                    Merge {{ selectedUserIds.length }} Users
                </v-btn>

                <v-btn 
                    v-if="has_searched"
                    outlined
                    class="d-inline ml-6 mt-2 light-blue-button"
                    color="primary"
                    @click="createUserDialog = true">
                    <v-icon class="mr-2">mdi-plus</v-icon> New User
                </v-btn>

            </div>

            <!-- SEARCH (LEFT) | DATE CREATED + STATUS FILTERS (RIGHT) -->
            <div class="pa-4 d-flex align-start" style="gap: 20px;">
                <div class="d-inline" style="width: 33%">
                    <v-text-field 
                        v-model="search"
                        hint="Search for an existing user by name, email or phone"
                        persistent-hint
                        color="primary darken-1" 
                        single-line 
                        append-icon="mdi-magnify">
                    </v-text-field>
                </div>

                <!-- <v-autocomplete
                    v-model="filterby.modules"
                    :items="all_modules"
                    item-value="module_id"
                    hint="Search by module"
                    persistent-hint
                    clearable
                    multiple
                    @change="filterUsersByModule"
                    class="mx-6"
                    style="width: 10%"
                    color="primary darken-1">

                    <template v-slot:selection="data">
                        <v-tooltip top>
                            <div style="font-size: .75rem">{{ data.item.module_key }}</div>
                            <template v-slot:activator="{ on }">
                                <v-chip
                                    v-on="on"
                                    v-bind="data.attrs"
                                    :input-value="data.selected"
                                    class="primary darken-1"
                                    small
                                    outlined>
                                    <customIcon :name="data.item.module_key" class="primary--text text--darken-2 icon-size-small" />
                                </v-chip>
                            </template>
                        </v-tooltip>
                    </template>
                    <template v-slot:item="data">
                        <template>
                            <customIcon :name="data.item.module_key" class="primary--text icon-size py-1 mr-4" />
                            <v-list-item-content>
                                {{ data.item.module_name_text }}
                            </v-list-item-content>
                        </template>
                    </template>
                </v-autocomplete> -->

                <v-spacer></v-spacer>

                <!-- Date Created Range Picker -->
                <v-menu
                    v-model="createdDateRangeMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :value="createdDateRangeText"
                            label="Date Created Range"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            clearable
                            @click:clear="clearCreatedDateRange"
                            hide-details
                            style="width: 300px"
                            color="primary darken-1"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        v-model="filterby.created_date_range"
                        range
                        no-title
                        scrollable
                        @input="filterby.created_date_range.length === 2 ? applyFilters() : null"
                    >
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="createdDateRangeMenu = false">Cancel</v-btn>
                        <v-btn text color="primary" @click="applyFilters(); createdDateRangeMenu = false">OK</v-btn>
                    </v-date-picker>
                </v-menu>

                <v-autocomplete
                    v-model="filterby.status"
                    :items="status_options"
                    label="Status"
                    hide-details
                    multiple
                    clearable
                    style="max-width: 300px"
                    color="primary darken-1"
                    @change="applyFilters()">
                </v-autocomplete>
            </div>

            <!-- COUNT OF USERS MATCHING THE SEARCH + FILTERS (ALWAYS SHOWN SO THE LAYOUT DOESN'T SHIFT) -->
            <div class="d-flex justify-end mt-1 mb-2 px-4">
                {{ filtered_users_cnt }} total result{{ filtered_users_cnt === 1 ? '' : 's' }}
            </div>
        </div>

        <v-data-table
            :headers="users_headers"
            :items="filteredUsers"
            v-model="selected_users"
            item-key="user_id"
            show-select
            :options.sync="options"
            :server-items-length="filtered_users_cnt"
            :footer-props="{ 'items-per-page-options': [25, 50, 100] }"
            :loading="table_loading"
            loading-text="Loading users ..."
            :height="'calc(100vh - 330px)'"
            fixed-header
            @click:row="editUser">

            <!-- SELECT-ALL CHECKBOX: EXPLAIN THAT ROW SELECTION IS FOR MERGING -->
            <template v-slot:[`header.data-table-select`]="{ props, on }">
                <v-tooltip top>
                    <template v-slot:activator="{ on: tooltip, attrs }">
                        <span v-bind="attrs" v-on="tooltip">
                            <v-simple-checkbox
                                v-bind="props"
                                v-on="on"
                                color="primary darken-1">
                            </v-simple-checkbox>
                        </span>
                    </template>
                    <span>Select 2 or more users to merge their accounts into one</span>
                </v-tooltip>
            </template>

            <template v-slot:no-data>
                <div class="text-center pt-12">
                    <v-icon large>mdi-alert-circle</v-icon>
                    <p class="mt-2">No users found matching your filters.</p>
                </div>
            </template>

            <template v-slot:no-results>
                <div class="text-center pt-12">
                    <v-icon large>mdi-alert-circle</v-icon>
                    <p class="mt-2">No tasks found matching your filters.</p>
                </div>
            </template>

            <template v-slot:[`item.full_name`]="{ item }">
                <div class="text-start">{{ item.full_name }}</div>
            </template>

            <template v-slot:[`item.phone`]="{ item }">
                <div class="text-end">{{ item.phone }}</div>
            </template>

            <template v-slot:[`item.user_email`]="{ item }">
                <div class="text-end">{{ item.user_email }}</div>
            </template>

            <template v-slot:[`item.company`]="{ item }">
                <div class="text-start">{{ item.company }}</div>
            </template>

            <!-- <template v-slot:[`item.module_icons`]="{ item }">
                <div class="justify-end d-flex">
                    <v-item
                        v-for="(mod, ndx) in item.module_icons"
                        :key="mod">
                        <v-tooltip top>
                            {{ ndx+1 }}
                            <div style="font-size: .75rem">{{mod}}</div>
                            <template v-slot:activator="{ on, attrs }">
                                <div class="icon-size pa-0 ma-0"
                                    v-on="on"
                                    v-bind="attrs">
                                    <customIcon :name='mod' 
                                        class="primary--text mb-n2" />
                                </div>
                            </template>
                        </v-tooltip>
                    </v-item>
                </div>
            </template> -->

            <template v-slot:[`item.created_date`]="{ item }">
                <div class="text-end">{{ item.created_date }}</div>
            </template>

            <template v-slot:[`item.is_active`]="{ item }">
                <div v-if="item.merged_into_user_id" class="text-end">Merged</div>
                <div v-else class="text-end">{{ item.is_active ? 'Yes' : 'No' }}</div>
            </template>

        </v-data-table>
    </div>
</template>

<script>
    import axios from "axios";
    import Cookie from 'js-cookie';
    import appUrls from '../helpers/environment';
    import CreateUser from './CreateUser';
    import MergeUsers from './MergeUsers';

    const USERS_SEARCH_KEY = 'admin_users_search'
    const USERS_FILTERS_KEY = 'admin_users_filters'

    export default {
        name: 'Users',
        
        components: {
            CreateUser,
            MergeUsers,
        },

        data: () => ({
            no_projects: false,
            has_searched: false,
            createUserDialog: false,
            createdDateRangeMenu: false,
            filtered_users_cnt: 0,
            status_options: ['Active', 'Inactive', 'Merged'],
            mergeUsersDialog: false,
            selected_users: [],
            table_loading: false,
            // SERVER-SIDE PAGING: THE API RETURNS ONE PAGE OF USERS + THE TOTAL THAT MATCH
            options: {
                sortBy: ['created_date'],
                sortDesc: [false],
                page: 1,
                itemsPerPage: 50,
                multiSort: true
            },
            search_timer: null,
            users_request_id: 0,
            users_query_key: '',
            users: [],
            users_headers: [
                { text: 'Full Name',     value: 'full_name',    align: 'start',   sortable: true },
                { text: 'Phone',         value: 'phone',        align: 'end',     sortable: true,  width: '10%' },
                { text: 'Email',         value: 'user_email',   align: 'end',     sortable: true  },
                { text: 'Company',       value: 'company',      align: 'start',     sortable: true  },
                // { text: 'Modules',       value: 'module_icons', align: 'end',     sortable: false, width: '5%'},
                { text: 'Date Created',  value: 'created_date', align: 'end',     sortable: true,  width: '10%'},
                { text: 'Active',        value: 'is_active',    align: 'end',     sortable: true,  width: '6%'  },                
                // { text: 'Module Access', value: '',             align: 'end',     sortable: false }
            ],
            filteredUsers: [],
            search: '',
            filterby: {
                clients: true,
                admins: false,
                sellers: false,
                buyers: false,
                status: [],
                created_date_range: [],
                modules: []
            },
            filter_by_status: ['review'],
            all_modules: [],
            assign_modules: [],
            states: [],
            titles: [],
            isSuperAdmin: false,
            isRootAdmin: false
        }),

        computed: {
            selectedUserIds () {
                return [...new Set(this.selected_users.map(u => u.user_id))]
            },
            // RESULTS SHOW ONLY ONCE SOMETHING NARROWS THE LIST - A SEARCH TERM, A COMPLETE DATE CREATED RANGE,
            // OR ONE OF THE SELLERS ONLY / ADMINS ONLY / BUYERS CHECKBOXES.
            // STATUS ONLY REFINES THOSE RESULTS, SO THE TABLE NEVER OPENS ON EVERY USER
            hasCriteria () {
                return !!this.search
                    || (this.filterby.created_date_range || []).length === 2
                    || this.filterby.sellers
                    || this.filterby.admins
                    || this.filterby.buyers
            },
            createdDateRangeText () {
                if ( !this.filterby.created_date_range || !this.filterby.created_date_range.length ) return '';
                return [...this.filterby.created_date_range].sort().join(' ~ ');
            }
        },

        watch: {
            'filterby.sellers' () { this.saveFilters() },
            'filterby.admins' () { this.saveFilters() },
            'filterby.status' () { this.saveFilters() },
            'filterby.created_date_range' () { this.saveFilters() },
            'filterby.buyers' () { this.saveFilters() },
            hasCriteria (active) {
                this.has_searched = active
            },
            // SORTING & PAGING ARE DONE BY THE API
            options: {
                deep: true,
                handler () { this.getUsers() }
            },
            search (val) {
                // REMEMBER THE SEARCH SO RETURNING TO THE USERS LIST RELOADS THE SAME RESULTS
                try {
                    if ( val ) {
                        localStorage.setItem(USERS_SEARCH_KEY, val)
                    } else {
                        localStorage.removeItem(USERS_SEARCH_KEY)
                    }
                } catch ( err ) {
                    console.log(err)
                }
                // WAIT FOR A PAUSE IN TYPING BEFORE ASKING THE API
                clearTimeout(this.search_timer)
                this.search_timer = setTimeout(() => this.applyFilters(), 350)
            }
        },

        created () {
            document.title = `Users`;
            this.adminOnly();

            // RESTORE FILTERS BEFORE THE SEARCH SO THE RELOADED RESULTS ARE FILTERED THE SAME WAY
            try {
                const saved_filters = JSON.parse(localStorage.getItem(USERS_FILTERS_KEY) || '{}')
                this.filterby.sellers = saved_filters.sellers === true
                this.filterby.admins = saved_filters.admins === true
                this.filterby.buyers = saved_filters.buyers === true
                this.filterby.status = Array.isArray(saved_filters.status)
                    ? saved_filters.status.filter(status => this.status_options.includes(status))
                    : []
                this.filterby.created_date_range = Array.isArray(saved_filters.created_date_range) && saved_filters.created_date_range.length === 2
                    ? saved_filters.created_date_range
                    : []

                const saved_search = localStorage.getItem(USERS_SEARCH_KEY)
                if ( saved_search ) this.search = saved_search
            } catch ( err ) {
                console.log(err)
            }
            this.has_searched = this.hasCriteria
            this.getUsers()
        },

        beforeDestroy () {
            clearTimeout(this.search_timer)
        },

        mounted () {
            this.getModules();
            // ONLY THE CREATE USER FORM NEEDS THESE, SO LOAD THEM WITHOUT HOLDING UP THE TABLE
            this.getLists();
        },
        methods: {
            adminOnly: async function () {
                try {
                    const modules_rs = await axios.get(`${appUrls.api}/api/admin/is_super/${Cookie.get('bool')}`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })

                    this.isRootAdmin = modules_rs.data.root;
                    this.isSuperAdmin = modules_rs.data.super;

                    // A RESTORED "ADMINS ONLY" FILTER IS HIDDEN FROM NON-SUPER ADMINS, SO DON'T APPLY IT
                    if ( !this.isSuperAdmin && this.filterby.admins ) {
                        this.filterby.admins = false
                        this.applyFilters()
                    }
                
                } catch ( error ) {
                    console.log(error);
                }
            },

            getModules: async function () {
                try {
                    const modules_rs = await axios.get(`${appUrls.api}/api/admin/submodule/false/all_modules`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })

                    //console.log(modules_rs.data.modules)
                    this.all_modules = modules_rs.data.modules;
                    for ( let m in this.all_modules ) {
                        this.all_modules[m].module_name_text = this.all_modules[m].module_name
                    }
                    this.assign_modules = this.all_modules.filter(match => match.module_key != 'buyer');
                
                } catch ( error ) {
                    console.log(error);
                }
            },

            // FETCH ONE PAGE OF USERS MATCHING THE SEARCH + FILTERS (NOTHING IS LOADED UNTIL SOMETHING NARROWS THE LIST).
            // force: REFRESH EVEN IF THE QUERY HASN'T CHANGED (AFTER A SAVE OR MERGE)
            getUsers: async function ( force = false ) {
                if ( !this.hasCriteria ) {
                    this.users_request_id++
                    this.users_query_key = ''
                    this.users = []
                    this.filteredUsers = []
                    this.filtered_users_cnt = 0
                    this.table_loading = false
                    return
                }

                const range = (this.filterby.created_date_range || []).length === 2 ? [...this.filterby.created_date_range].sort() : []
                const params = {
                    search: this.search || undefined,
                    sellers: this.filterby.sellers || undefined,
                    admins: this.filterby.admins || undefined,
                    buyers: this.filterby.buyers || undefined,
                    status: (this.filterby.status || []).length ? this.filterby.status : undefined,
                    created_from: range[0],
                    created_to: range[1],
                    page: this.options.page,
                    per_page: this.options.itemsPerPage,
                    sort_by: this.options.sortBy,
                    sort_desc: this.options.sortDesc
                }

                // THE TABLE RE-EMITS ITS OPTIONS & THE SEARCH IS DEBOUNCED, SO SKIP A REQUEST IDENTICAL TO THE LAST ONE
                const query_key = JSON.stringify(params)
                if ( !force && query_key === this.users_query_key ) return
                this.users_query_key = query_key

                const request_id = ++this.users_request_id
                this.table_loading = true
                try {
                    const users_rs = await axios.get(`${appUrls.api}/api/admin/users_search`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        params
                    })

                    // A NEWER REQUEST STARTED WHILE THIS ONE WAS IN FLIGHT
                    if ( request_id !== this.users_request_id ) return

                    if ( users_rs.data.outcome && Array.isArray(users_rs.data.users) ) {
                        const users = users_rs.data.users.map(user => ({
                            ...user,
                            full_name: `${user.first_name} ${user.last_name}`,
                            created_date: String(user.created_date || '').substr(0, 10),
                            status: user.merged_into_user_id ? 'Merged' : user.is_active ? 'Active' : 'Inactive',
                            roles: user.roles || [],
                            // GROUP | ROLES
                            group: Number((user.roles || [])[0])
                        }))
                        this.users = users
                        this.filteredUsers = users
                        this.filtered_users_cnt = users_rs.data.total

                        // THE FILTERS SHRANK THE RESULTS BELOW THE CURRENT PAGE -> GO BACK TO PAGE 1
                        if ( !users.length && this.filtered_users_cnt === 0 && this.options.page > 1 ) {
                            this.options = { ...this.options, page: 1 }
                        }
                    } else {
                        this.users_query_key = ''
                        this.triggerUserFeedback( true, 'There was a problem loading the users.', 'accent' );
                    }
                } catch ( error ) {
                    if ( request_id !== this.users_request_id ) return
                    console.log(error);
                    // LET THE SAME QUERY BE RETRIED
                    this.users_query_key = ''
                    this.triggerUserFeedback( true, 'There was a problem loading the users.', 'accent' );
                }
                this.table_loading = false;
            },

            // A FILTER OR THE SEARCH CHANGED: START AGAIN FROM PAGE 1 (CHANGING options.page TRIGGERS THE FETCH)
            applyFilters () {
                if ( this.options.page !== 1 ) {
                    this.options = { ...this.options, page: 1 }
                } else {
                    this.getUsers()
                }
            },

            getProjects: async function (user_id) {
                try {
                    const projects_rs = await axios.get(`${appUrls.api}/api/admin/user_projects/${user_id}`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" }
                    })

                    if(projects_rs.data.outcome) {
                        setTimeout(() => {
                            this.projects = projects_rs.data.projects;
                        }, 1000)
                    } else {
                        this.no_projects = true;
                    }
                } catch ( error ) {
                    console.log(error);
                }
            },
 
            getLists: async function () {
                try {
                    let optionsLists = [ 
                        {'name': 'states', 'type': 'txt'},
                        {'name': 'titles', 'type': 'txt'},
                    ];

                    const these_lists = await axios.post(`${appUrls.api}/api/lists`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        list_options: optionsLists
                    })

                    if(these_lists.data.outcome){
                        const arrLists = these_lists.data.lists;                        
                        // SET PROPERTIES WITH THIER APPROPRIATE LIST
                        for (let l in arrLists) {
                            if (arrLists[l].name == 'states') this.states = arrLists[l].list
                            if (arrLists[l].name == 'titles') this.titles = arrLists[l].list
                        }
                    }
                } catch ( error ) {
                    console.log(error);
                }
            },

            saveData: async function (data) {
                try {
                    const save_user_data_rs = await axios.post(`${appUrls.api}/api/admin/save`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        save: data
                    })

                    //console.log(save_user_data_rs.data)
                    if ( save_user_data_rs.data.outcome ) {
                        let user_data = save_user_data_rs.data.user;
                        
                        if (user_data.org_index == '') {
                            this.createUserDialog = false;
                        }
                        this.getUsers( true );

                        setTimeout(() => {
                            this.getProjects(user_data.user_id);
                        }, 2000)

                        this.triggerUserFeedback( true, save_user_data_rs.data.message, 'success' );

                    } else {
                        this.triggerUserFeedback( true, save_user_data_rs.data.message, 'accent' );
                    }
                
                } catch ( err ) {
                    if ( err.error.code == '23505' ) {
                        this.triggerUserFeedback( true, 'The email address must be unique!', 'accent' );
                    }
                    //console.log(err);
                }
            },

            clearCreatedDateRange () {
                this.filterby.created_date_range = [];
                this.applyFilters();
            },

            filterUsersByModule: async function () {
                //console.log(this.filterby.modules.length)
                let user_modules = this.filterby.modules.length ? this.filterby.modules : false;

                // FILTERING MAGIC BASED ON ROLES
                if(user_modules) {
                    // FIRST FILTER OUT ADMINS & RESET this.filteredUsers
                    if(this.filterby.admins) {
                        this.filteredUsers = this.users.filter(r => { 
                            return r.roles.some( g => { return [1,2,3,6].includes(g) } )
                        })
                    } else if (this.filterby.sellers) {
                        this.filteredUsers = this.users.filter(r => { 
                            return r.roles.some( g => { return [10].includes(g) } )
                        })
                    } else {
                        this.filteredUsers = this.users.filter(r => { 
                            return r.roles.some( g => { return [4,5,7].includes(g) } )
                        })
                    }

                    // THEN FILTER BY MODULES - THIS CREATES THE "OR" SCENARIO INSTEAD OF AND
                    this.filteredUsers = this.filteredUsers.filter(row => {
                        return row.modules.find(m => user_modules.includes(m))
                    });
                    this.table_loading = false;
                } else {
                    this.applyFilters()
                    // this.filteredUsers = this.users;
                }
            },

            usersMerged (merge_data) {
                this.mergeUsersDialog = false
                this.selected_users = []
                this.triggerUserFeedback( true, merge_data.message, 'success' )
                this.getUsers( true )
            },

            editUser (item) {
                this.$router.push({ path: `/user_details/${item.user_id}`});
            },

            userActive: async function (item, value) {
                try {
                    this.$feedback.hide();

                    item.is_active = value
                    item.org_index = this.users.indexOf(item);
                    // console.log(item)
                    const user_active_rs = await axios.put(`${appUrls.api}/api/admin/active`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        update: item
                    })
                    
                    if ( user_active_rs.data.outcome ) {
                        this.triggerUserFeedback( true, user_active_rs.data.message, 'success' );

                        let user_data = user_active_rs.data.user;
                        if (user_data.org_index > -1) {
                            Object.assign(this.users[user_data.org_index], user_data);
                        } else {
                            this.users.push(user_data);
                        }
                    } else {
                        this.triggerUserFeedback( true, user_active_rs.data.message, 'accent' )
                    }
                
                } catch ( error ) {
                    console.log(error);
                    this.triggerUserFeedback( true, 'An error occurred while updating user status.', 'accent' );
                }
            },

            saveFilters () {
                try {
                    localStorage.setItem(USERS_FILTERS_KEY, JSON.stringify({
                        sellers: this.filterby.sellers,
                        admins: this.filterby.admins,
                        buyers: this.filterby.buyers,
                        status: this.filterby.status || [],
                        // ONLY A COMPLETE RANGE IS WORTH RESTORING
                        created_date_range: this.filterby.created_date_range.length === 2 ? this.filterby.created_date_range : []
                    }))
                } catch ( err ) {
                    console.log(err)
                }
            },

            triggerUserFeedback: function ( feedback_trigger, feedback_message, feedback_color='light-green' ) {
                this.$feedback.trigger( feedback_trigger, feedback_message, feedback_color );
            }
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    #users_wrapper {
        width: 100%;
        margin: 0 auto;
        padding: 3rem 0 0 0;
        display: flex;
        flex-direction: column;
        height: 100vh;
    }
    /* .{
        background-color: rgba(223, 233, 236, .25)
    } */
    tr:nth-of-type(odd) {
        background-color: rgba(223, 233, 236, .25);
    }
    >>> tbody tr {
        cursor: pointer !important;
    }
    td.no_extra_border {
        border-bottom: 0 !important;
    }
    .adminOnly >>> label {
        font-size: .9rem;
    }
    .bg_pewter {
        background-color: #E9EAEC;
    }
    .icon-size {
        width: 3rem;
    }
    .icon-size-small {
        width: 2.25rem;
    }
    .icon-size-header {
        width: 2.5rem;
    }
    .icon-center {
        margin: 0 auto;
    }
    .item-row {
        display: table-row;
    }

    .drawer-shadow {
        position: fixed !important;
        top: 0 !important;
        right: 0 !important;
        height: 100vh !important;
        z-index: 2000;
        box-shadow: -6px 0 12px rgba(0, 0, 0, 0.15);
    }
    
    .light-blue-button {
        background-color: #eef7f8;
    }
</style>


// WEBPACK FOOTER //
// src/components/Users.vue